// Shared visual identity for the AI participant's inline contributions — used by
// both the chat cards (TranscriptView) and the bottom scrubber (SignalTimeline),
// so the colour/icon of an "Insight" is the same in the feed and on the timeline.
// Kept in its own module so the two components don't import each other (cycle).

import { AlertTriangle, Info, Lightbulb, Link2, Sparkles } from 'lucide-react'
import type { AiTurnKind } from '@/lib/ai/types'

export type Tone = 'accent' | 'success' | 'warn' | 'danger' | 'muted'

// Card tones — left-accent + tint + label colour. Full literals so Tailwind's
// scanner keeps the classes.
export const TONE: Record<Tone, { border: string; bg: string; text: string }> = {
  accent: { border: 'border-accent/40 border-l-accent', bg: 'bg-accent/5', text: 'text-accent' },
  success: { border: 'border-success/40 border-l-success', bg: 'bg-success/5', text: 'text-success' },
  warn: { border: 'border-warn/40 border-l-warn', bg: 'bg-warn/5', text: 'text-warn' },
  danger: { border: 'border-danger/40 border-l-danger', bg: 'bg-danger/5', text: 'text-danger' },
  muted: { border: 'border-border border-l-muted-foreground', bg: 'bg-surface', text: 'text-muted-foreground' }
}

// Per-kind tone + icon + label. `present` is the AI bubble; the rest are typed
// cards. The scrubber reuses this for its markers and legend.
export const KIND_STYLE: Record<
  AiTurnKind,
  { tone: Tone; Icon: typeof Lightbulb; label: string }
> = {
  candidate: { tone: 'accent', Icon: Lightbulb, label: 'Idea' },
  insight: { tone: 'success', Icon: Link2, label: 'Insight' },
  caution: { tone: 'warn', Icon: AlertTriangle, label: 'Caution' },
  note: { tone: 'muted', Icon: Info, label: 'Note' },
  present: { tone: 'accent', Icon: Sparkles, label: 'AI participant' }
}

// Marker chip colours for the scrubber (literal classes so Tailwind keeps them).
export const TONE_CHIP: Record<Tone, string> = {
  accent: 'border-accent bg-accent/20 text-accent',
  success: 'border-success bg-success/20 text-success',
  warn: 'border-warn bg-warn/20 text-warn',
  danger: 'border-danger bg-danger/20 text-danger',
  muted: 'border-muted-foreground bg-muted-foreground/20 text-muted-foreground'
}
